import React, { useState } from 'react';
import Layout from 'Layout';
import './Download.scss';

type Platform = 'MT4' | 'MT5' | 'cTrader' | 'ThinkTrader';

interface DownloadOption {
  platform: 'iOS' | 'Android' | 'MacOS' | 'Windows';
  label: string;
  icon: string;
  url?: string;
}

const DownloadPage: React.FC = () => {
  const [selectedPlatform, setSelectedPlatform] = useState<Platform | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const platforms = [
    { id: 'MT4' as Platform, name: 'MetaTrader 4', logo: 'MT4' },
    { id: 'MT5' as Platform, name: 'MetaTrader 5', logo: 'MT5' },
    { id: 'cTrader' as Platform, name: 'cTrader', logo: 'cTrader' },
    { id: 'ThinkTrader' as Platform, name: 'ThinkTrader', logo: 'ThinkTrader' },
  ];

  const getPlatformIcon = (platform: 'iOS' | 'Android' | 'MacOS' | 'Windows') => {
    if (platform === 'iOS' || platform === 'MacOS') {
      return (
        <img src="/images/logo-apple.svg" alt={platform} />
      );
    }
    if (platform === 'Android') {
      return (
        <img src="/images/logo-android.svg" alt="Android" />
      );
    }
    if (platform === 'Windows') {
      return (
        <img src="/images/logo-windows.svg" alt="Windows" />
      );
    }
    return null;
  };

  const linkDownload = [
    {
      id : 'MT4',
      links: {
        iOS: 'https://apps.apple.com/app/metatrader-4/id496212596',
        Android: 'https://play.google.com/store/apps/details?id=net.metaquotes.metatrader4',
        MacOS: 'https://download.terminal.free/cdn/web/metaquotes.software.corp/mt4/MetaTrader4.pkg.zip?utm_campaign=metatrader4.help',
        Windows: 'https://www.metatrader4.com/en/download'
      }
    },
    {
      id : 'MT5',
      links: {
        iOS: 'https://download.mql5.com/cdn/mobile/mt5/ios?hl=en&utm_source=www.metatrader5.com&utm_campaign=install.metaquotes',
        Android: 'https://download.mql5.com/cdn/mobile/mt5/android?hl=en&utm_source=www.metatrader5.com&utm_campaign=install.metaquotes',
        MacOS: 'https://download.mql5.com/cdn/web/metaquotes.ltd/mt5/MetaTrader5.pkg.zip?utm_source=www.metatrader5.com&utm_campaign=download.mt5.macos',
        Windows: 'https://download.mql5.com/cdn/web/metaquotes.ltd/mt5/mt5setup.exe?utm_source=www.metatrader5.com&utm_campaign=download'
      }
    },
    {
      id : 'cTrader',
      links: {
        iOS: 'https://apps.apple.com/cy/app/ctrader-cfd-trading-charts/id767428811',
        Android: 'https://play.google.com/store/apps/details?id=com.spotware.ct&hl=en',
        MacOS: 'https://getctradermac.spotware.com/ctrader-spotware-setup.dmg?version=1786958965858',
        Windows: 'https://getctraderwin.spotware.com/ctrader-spotware-setup.exe?version=1786958965858'
      }
    },
    {
      id : 'ThinkTrader',
      links: {
        iOS: 'https://play.google.com/store/apps/details?id=com.riflexo.tradeinterceptormobile&hl=en',
        Android: 'https://apps.apple.com/vn/app/thinktrader/id329476057?l=vi',
        MacOS: '/file/ThinkTrader.zip',
        Windows: '/file/ThinkTrader.zip'
      }
    }
  ]

  const downloadOptions: Record<Platform, DownloadOption[]> = {
    MT4: [
      { platform: 'iOS', label: 'iOS', icon: 'iOS' },
      { platform: 'Android', label: 'Android', icon: 'Android' },
      { platform: 'MacOS', label: 'MacOS', icon: 'MacOS' },
      { platform: 'Windows', label: 'Windows', icon: 'Windows' },
    ],
    MT5: [
      { platform: 'iOS', label: 'iOS', icon: 'iOS' },
      { platform: 'Android', label: 'Android', icon: 'Android' },
      { platform: 'MacOS', label: 'MacOS', icon: 'MacOS' },
      { platform: 'Windows', label: 'Windows', icon: 'Windows' },
    ],
    cTrader: [
      { platform: 'iOS', label: 'iOS', icon: 'iOS' },
      { platform: 'Android', label: 'Android', icon: 'Android' },
      { platform: 'MacOS', label: 'MacOS', icon: 'MacOS' },
      { platform: 'Windows', label: 'Windows', icon: 'Windows' },
    ],
    ThinkTrader: [
      { platform: 'iOS', label: 'iOS', icon: 'iOS' },
      { platform: 'Android', label: 'Android', icon: 'Android' },
      { platform: 'MacOS', label: 'MacOS', icon: 'MacOS' },
      { platform: 'Windows', label: 'Windows', icon: 'Windows' },
    ],
  };

  const handlePlatformClick = (platform: Platform) => {
    setSelectedPlatform(platform);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedPlatform(null);
  };

  const handleDownload = (option: DownloadOption) => {
    if (selectedPlatform) {
      const platformLinks = linkDownload.find(p => p.id === selectedPlatform);
      const downloadUrl = platformLinks?.links[option.platform];
      if (downloadUrl) {
        window.open(downloadUrl, '_blank');
      }
    }
  };

  const getPlatformLogo = (platform: Platform) => {
    const logoMap: Record<Platform, string> = {
      MT4: '/images/bot-store/logo-mt4.png',
      MT5: '/images/bot-store/logo-mt5.png',
      cTrader: '/images/bot-store/logo-ctrader.png',
      ThinkTrader: '/images/bot-store/thinktrader.png',
    };

    return (
      <div className="platform-logo">
        <img src={logoMap[platform]} alt={platform} />
      </div>
    );
  };

  return (
    <Layout>
      <div className="download-bots-page">
        <div className="download-bots-container">
          <h1 className="page-title">Các nền tảng</h1>
          
          <div className="platforms-list">
            {platforms.map((platform) => (
              <div key={platform.id} className="platform-item">
                <div className="platform-info">
                  {getPlatformLogo(platform.id)} {
                    platform.name == 'ThinkTrader' && <span className="platform-name">ThinkTrader</span> 
                  }
                </div>
                <button
                  className="platform-button"
                  onClick={() => handlePlatformClick(platform.id)}
                >
                  Các nền tảng được hỗ trợ
                </button>
              </div>
            ))}
          </div>
        </div>

        {isModalOpen && selectedPlatform && (
          <div className="download-modal-overlay" onClick={handleCloseModal}>
            <div className="download-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <span className="modal-title">Nền tảng: {selectedPlatform}</span>
                <button className="modal-close" onClick={handleCloseModal}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M18 6L6 18M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="modal-content">
                <div className="download-section">
                  <h3 className="section-title">Mobile</h3>
                  <div className="download-buttons">
                    {downloadOptions[selectedPlatform]
                      .filter((opt) => opt.platform === 'iOS' || opt.platform === 'Android')
                      .map((option) => (
                        <button
                          key={option.platform}
                          className="download-button"
                          onClick={() => handleDownload(option)}
                        >
                          <span className="download-icon">{getPlatformIcon(option.platform)}</span>
                          <div className="download-text">
                            <span className="download-label">Tải xuống cho</span>
                            <span className="download-platform">{option.label}</span>
                          </div>
                        </button>
                      ))}
                  </div>
                </div>

                <div className="download-section">
                  <h3 className="section-title">Desktop</h3>
                  <div className="download-buttons">
                    {downloadOptions[selectedPlatform]
                      .filter((opt) => opt.platform === 'MacOS' || opt.platform === 'Windows')
                      .map((option) => (
                        <button
                          key={option.platform}
                          className="download-button"
                          onClick={() => handleDownload(option)}
                        >
                          <span className="download-icon">{getPlatformIcon(option.platform)}</span>
                          <div className="download-text">
                            <span className="download-label">Tải xuống cho</span>
                            <span className="download-platform">{option.label}</span>
                          </div>
                        </button>
                      ))}
                  </div>
                </div>
              </div>

              <div className="modal-footer">
                <button className="close-button" onClick={handleCloseModal}>
                  Đóng
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default DownloadPage;

